import {
  TFunction,
  CalendarState,
} from '../../components/BookingCalendar/controller';
import { SelectedBookingPreference } from '../bookingPreferences/bookingPreferences';
import { getSelectedValuesOfAllBookingPreferences } from '../selectedBookingPreferences/selectedBookingPreferences';
import { Optional } from '../../types/types';
import { getSlotDuration } from '../duration/duration';
import {
  DO_NOT_CARE_STAFF,
  STAFF_RESOURCE_ID,
} from '../../constants/constants';
import {
  TimeSlot,
  LocationType,
} from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { getStaffList } from '../slotAvailability/slotAvailability';
import { CalendarContext } from '../context/contextFactory';
import {
  DisplayTarget,
  PreferenceType,
} from '@wix/bookings-services-preferences-modal/utils';
import { isBookFlowSettingsEnabled as isBookFlowSettingsEnabledUtil } from '@wix/bookings-calendar-catalog-viewer-mapper';

export const getSelectedSlots = ({
  selectableSlotsAtSelectedTime,
  dateRegionalSettingsLocale,
  t,
  selectedBookingPreferences,
  context,
  state,
}: {
  selectableSlotsAtSelectedTime: TimeSlot[];
  dateRegionalSettingsLocale: string;
  t: TFunction;
  selectedBookingPreferences: SelectedBookingPreference[];
  context?: CalendarContext;
  state?: CalendarState;
}): TimeSlot[] => {
  const [selectedLocation, selectedStaffMember, selectedDuration] =
    getSelectedValuesOfAllBookingPreferences(selectedBookingPreferences);

  const [
    locationSelectedOnCalendar,
    staffSelectedOnCalendar,
    durationSelectedOnCalendar,
  ] = getCalendarPreferencesVisibility(context, state);

  const selectedSlots = selectableSlotsAtSelectedTime.filter(
    (selectableSlot: TimeSlot) => {
      return (
        isSlotLocationSelected(
          selectableSlot,
          selectedLocation,
          locationSelectedOnCalendar,
        ) &&
        isSlotStaffSelected(
          selectableSlot,
          selectedStaffMember,
          staffSelectedOnCalendar,
        ) &&
        isSlotDurationSelected(
          selectableSlot,
          selectedDuration,
          dateRegionalSettingsLocale,
          t,
          durationSelectedOnCalendar,
        )
      );
    },
  );

  // in order to keep the structure of before of flat slot list by staff, we need to remove the staff that we don't need
  if (
    selectedSlots.length > 0 &&
    selectedStaffMember &&
    selectedStaffMember !== DO_NOT_CARE_STAFF
  ) {
    selectedSlots.forEach((slot) => {
      slot.availableResources?.forEach((resourceType) => {
        if (resourceType.resourceTypeId === STAFF_RESOURCE_ID) {
          resourceType.resources = resourceType.resources?.filter(
            (resource) => {
              return resource.id === selectedStaffMember;
            },
          );
        }
      });
    });
  }

  return selectedSlots;
};

const isSlotLocationSelected = (
  slot: Optional<TimeSlot>,
  selectedLocation: Optional<string>,
  selectedOnCalendar: boolean,
) => {
  if (!selectedOnCalendar || !selectedLocation) {
    return true;
  }

  const isAtClientPlace =
    slot?.location?.locationType === LocationType.CUSTOMER;
  const isSlotOnSelectedLocation =
    isAtClientPlace ||
    slot?.location?.id === selectedLocation ||
    slot?.location?.formattedAddress === selectedLocation;

  return isSlotOnSelectedLocation;
};

const isSlotStaffSelected = (
  slot: Optional<TimeSlot>,
  selectedStaffMember: Optional<string>,
  selectedOnCalendar: boolean,
) => {
  if (
    !selectedOnCalendar ||
    selectedStaffMember === DO_NOT_CARE_STAFF ||
    !selectedStaffMember
  ) {
    return true;
  }

  const isSlotWithSelectedStaffMember = getStaffList(slot ?? {}).some(
    (staff) => staff.id === selectedStaffMember,
  );

  return isSlotWithSelectedStaffMember;
};

const isSlotDurationSelected = (
  slot: Optional<TimeSlot>,
  selectedDuration: Optional<string>,
  dateRegionalSettingsLocale: string,
  t: TFunction,
  selectedOnCalendar: boolean,
) => {
  if (!selectedOnCalendar || !selectedDuration) {
    return true;
  }

  const isSlotWithSelectedDuration =
    getSlotDuration({
      rfcStartTime: slot?.localStartDate!,
      rfcEndTime: slot?.localEndDate!,
      t,
      dateRegionalSettingsLocale,
    }).durationText === selectedDuration;

  return isSlotWithSelectedDuration;
};

const getCalendarPreferencesVisibility = (
  context: CalendarContext | undefined,
  state: CalendarState | undefined,
): [boolean, boolean, boolean] => {
  if (!context || !state) {
    return [true, true, true];
  }

  const { experiments, bookingsSettings } = context;
  const { appointmentPreferencesDisplay } = state;

  const isBookFlowSettingsEnabled = isBookFlowSettingsEnabledUtil({
    experiments,
    bookingsSettings,
  });

  if (!isBookFlowSettingsEnabled || !appointmentPreferencesDisplay) {
    return [true, true, true];
  }

  return [
    appointmentPreferencesDisplay[PreferenceType.LOCATION] ===
      DisplayTarget.CALENDAR,
    appointmentPreferencesDisplay[PreferenceType.STAFF_MEMBER] ===
      DisplayTarget.CALENDAR,
    appointmentPreferencesDisplay[PreferenceType.DYNAMIC_PRICE] ===
      DisplayTarget.CALENDAR,
  ];
};
